/** Defend step: what drives the price and cost, where the margin sits, and how
 *  far the total moves when the main inputs change. */
import React, { useMemo, useState } from 'react';
import { ProjectState, Spread } from './spreadSplitData';
import { SpreadPriceState } from './useSpreadPrice';
import { SpreadRequestOpts } from './spreadPricingApi';
import { buildDefence } from './spreadDefence';
import { runSensitivity } from './spreadSensitivity';
import { DefenceDrivers } from './DefenceDrivers';
import { DefenceSensitivity, SensitivityState } from './DefenceSensitivity';
import { MachineMixTable } from './MachineMixTable';
import { projectMachineLines } from './weldingMachines';
import { CRC } from './moduleInputsData';

const card: React.CSSProperties = {
  background: '#fff', border: `1px solid ${CRC.border}`, borderRadius: 12,
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)', padding: 20,
};

interface Props {
  project: ProjectState;
  priceStates: Record<string, SpreadPriceState>;
  servicesTotal: number;
  opts: SpreadRequestOpts;
  baseMonths: (s: Spread) => number;
  /** Spreads still waiting on the engine. Sensitivity needs a settled base. */
  pending: number;
}

export const StepDefend: React.FC<Props> = ({ project, priceStates, servicesTotal, opts, baseMonths, pending }) => {
  const [sensitivity, setSensitivity] = useState<SensitivityState>({ status: 'idle' });

  const defence = useMemo(
    () => buildDefence(project, priceStates, servicesTotal),
    [project, priceStates, servicesTotal]
  );

  const handleRun = async () => {
    setSensitivity({ status: 'running', done: 0, total: 0 });
    try {
      const result = await runSensitivity({
        project,
        opts,
        baseTotal: defence.totalPrice,
        servicesTotal,
        baseMonths,
        onProgress: (done, total) => setSensitivity({ status: 'running', done, total }),
      });
      setSensitivity({ status: 'done', result });
    } catch (err) {
      setSensitivity({
        status: 'error',
        message: err instanceof Error ? err.message : 'Could not re-price the project.',
      });
    }
  };

  const machines = useMemo(() => projectMachineLines(project, priceStates), [project, priceStates]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 760 }}>
      <DefenceDrivers defence={defence} />

      {machines.length > 0 && (
        <div style={card}>
          <div style={{ fontFamily: 'Funnel Sans', fontSize: 15, fontWeight: 600, color: CRC.blueprintBlue }}>The machine mix</div>
          <div style={{ fontSize: 11.5, color: CRC.heritageGrey, marginTop: 3, marginBottom: 12 }}>
            Every machine across every spread, ranked by what it contributes to the price. Machines that cost more to buy than they earn back over this duration show a negative margin.
          </div>
          <MachineMixTable rows={machines} />
        </div>
      )}

      <DefenceSensitivity
        state={sensitivity}
        onRun={handleRun}
        disabledReason={pending > 0 ? 'Waiting for every spread to price before comparing against a base total.' : undefined}
      />
    </div>
  );
};
